import { Templates, Utils } from '@hellohair/types';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DataContext } from '../../../../aws/DataProvider';
import { deepEqual } from '../../../../helpers/deepEqual';

export interface StepDraft<T> {
  /** The in-progress edit of the step. Not persisted until `save` is called. */
  draft: T;
  /** Updates a single field of the draft. */
  set: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Replaces the whole draft - for the array-shaped fields. */
  patch: (values: Partial<T>) => void;
  /** Writes the draft back onto the survey step at `index`. */
  save: () => void;
  /** True once the draft differs from the step as it was opened. */
  dirty: boolean;
}

/**
 * Holds the local edit state for one step. Unlike the previous per-component
 * `useState` piles it keeps the whole step object, so fields a given editor
 * does not surface survive a save instead of being silently dropped.
 */
export function useStepDraft<T extends Templates.StepDataBase>(
  survey: Templates.SurveyTemplate,
  index: number,
  /** Normalises legacy shapes (e.g. `accept` stored as a bare string). */
  normalise?: (step: T) => T
): StepDraft<T> {
  const {
    formDetailsState: [, setCurrentSurvey],
  } = useContext(DataContext);

  const step = survey.steps[index] as T;
  // Frozen at mount: the baseline the dirty check compares against, so a
  // normalised legacy field does not count as an edit the user made.
  const [initial] = useState<T>(() => (normalise ? normalise(step) : step));
  const [draft, setDraft] = useState<T>(initial);

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const patch = useCallback((values: Partial<T>) => {
    setDraft((prev) => ({ ...prev, ...values }));
  }, []);

  const save = useCallback(() => {
    const steps = [...survey.steps];
    steps[index] = draft;
    setCurrentSurvey({ ...survey, steps } as Utils.DBItem<Templates.SurveyTemplate>);
  }, [survey, index, draft, setCurrentSurvey]);

  const dirty = useMemo(() => !deepEqual(draft, initial), [draft, initial]);

  return { draft, set, patch, save, dirty };
}
